import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { CommonModule } from "@angular/common";
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from "@angular/forms";
import {
  NbButtonModule,
  NbIconModule,
  NbInputModule,
  NbCardModule,
  NbSelectModule,
  NbOptionModule,
  NbFormFieldModule,
  NbDatepickerModule,
} from "@nebular/theme";
import { ApiService } from "../../services/api/api.service";
import { StateService } from "../../@core/utils";
import { handleInviteError, handleInviteResponse, InviteResult, toBirthDateString } from "../../../hh_platform_frontend/docs/app/hh_utils";
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { StatusIconDirective } from "../../directives/status-icon.directive";

@Component({
  selector: "patient-invite",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NbButtonModule,
    NbIconModule,
    NbInputModule,
    NbCardModule,
    NbSelectModule,
    NbOptionModule,
    NbFormFieldModule,
    NbDatepickerModule,
    TranslateModule,
    StatusIconDirective,
  ],
  templateUrl: "./patient-invite.component.html",
  styleUrls: ["./patient-invite.component.scss"],
})
export class PatientInviteComponent implements OnInit {
  showForm = false;
  inviteSuccess = false;
  inviteError = "";
  inviteForm: FormGroup;
  isLoading = false;
  successMessage = "";

  // Upper bound for the birthdate datepicker — a birthdate can't be in the future.
  today = new Date();

  allowedPrincipals: Array<{
    id: number;
    documentId: string;
    firstName: string;
    lastName: string;
    email: string;
  }> = [];
  selectedPrincipalId: string | null = null;

  @Output() enableOrDisableSearch = new EventEmitter<boolean>();

  setSearchEnabled(enabled: boolean) {
    this.enableOrDisableSearch.emit(enabled);
  }

  constructor(
    private fb: FormBuilder,
    public api: ApiService,
    public state: StateService,
    public translate: TranslateService,
  ) {
    this.inviteForm = this.fb.group({
      email: [
        "",
        [
          Validators.required,
          Validators.email,
          Validators.pattern("[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}"),
        ],
      ],
      firstName: ["", Validators.required],
      lastName: ["", Validators.required],
      birthDate: [null, Validators.required],
    });
  }

  ngOnInit() {
    this.loadAllowedPrincipals();
    this.showForm = true;
  }

  get user() {
    return this.state.user;
  }

  get isAssistant(): boolean {
    return this.user?.role?.type === "assistant";
  }

  get isProfessional(): boolean {
    return this.user?.role?.type === "authenticated_professional";
  }

  get isAssistantWithNoEmployers(): boolean {
    if (this.isAssistant && this.allowedPrincipals.length === 0) {
      this.setSearchEnabled(false);
      return true;
    } else {
      this.setSearchEnabled(true);
      return false;
    }
  }

  get canInvite(): boolean {
    return (
      this.isProfessional ||
      (this.isAssistant && this.allowedPrincipals.length > 0)
    );
  }

  async loadAllowedPrincipals() {
    if (!this.isAssistant) return;

    try {
      const response = await this.api.getAllowedPrincipals();
      this.allowedPrincipals = response?.data?.data || [];

      // Start with the pinned doctor, or the only one available
      this.selectedPrincipalId = this.state.pickDefaultPrincipalId(this.allowedPrincipals);
    } catch (error) {
      console.deverror("Error loading allowed principals:", error);
    }
  }

  enableInviteMode() {
    this.showForm = true;
    this.inviteSuccess = false;
    this.inviteError = "";
  }

  async sendInvite() {
    if (this.inviteForm.invalid) return;
    if (
      this.isAssistant &&
      this.allowedPrincipals.length > 1 &&
      !this.selectedPrincipalId
    ) {
      this.inviteError = this.translate.instant('groups.error.selectDoctor');
      return;
    }

    this.inviteError = "";
    this.successMessage = "";
    this.isLoading = true;

    const { email, firstName, lastName, birthDate } = this.inviteForm.value;

    let principalId: string | undefined;

    if (this.isProfessional) {
      principalId = this.user.documentId;
    } else if (this.isAssistant) {
      principalId = this.selectedPrincipalId || this.allowedPrincipals[0]?.documentId;
    }

    try {
      const respose = await this.api.inviteUserWithCode(
        email,
        firstName,
        lastName,
        toBirthDateString(birthDate),
        "Authenticated",
        principalId
      );

      const result: InviteResult = handleInviteResponse(respose, firstName, email);
      this.successMessage = this.translate.instant(result.messageKey, result.messageParams)
      this.inviteSuccess = true;
      this.showForm = false;
    } catch (error: any) {
      const result = handleInviteError(error);
      this.inviteError = this.translate.instant(result.messageKey, result.messageParams);
    } finally {
      this.isLoading = false;
    }
  }

  cancelInvite() {
    this.showForm = false;
    this.inviteForm.reset();
    this.inviteError = "";
  }

  resetForm() {
    this.inviteSuccess = false;
    this.inviteForm.reset();
    this.inviteError = "";
    this.selectedPrincipalId = this.state.pickDefaultPrincipalId(this.allowedPrincipals);
  }
}
